import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import { isAxiosError } from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import { Button } from "components/common/Button";
import { useThemedLogo } from "components/hooks/useThemedLogo";
import { isPasswordValid } from "@/components/authentication/common/passwordValidation";

export const ResetPasswordPage = () => {
  const Logo = useThemedLogo();
  const [password, setPassword] = useState("");
  const [password_confirm, setPasswordConfirm] = useState("");
  const [message, setMessage] = useState({ text: "", type: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const navigate = useNavigate();
  const { token } = useParams();

  useEffect(() => {
    if (!token) {
      setMessage({ text: "Invalid reset token", type: "error" });
    }
  }, [token]);

  const validatePassword = (pwd: string) => isPasswordValid(pwd);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setMessage({ text: "Invalid reset token", type: "error" });
      return;
    }

    if (!password || !password_confirm) {
      setMessage({ text: "Please fill in all fields.", type: "error" });
      return;
    }

    if (password !== password_confirm) {
      setMessage({ text: "Passwords do not match.", type: "error" });
      return;
    }

    if (!validatePassword(password)) {
      setMessage({
        text: "Password is not strong enough.",
        type: "error",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axiosInstance.post(
        `/api/auth/resetpassword/${token}`,
        { password, password_confirm },
      );
      if (response.status === 200) {
        setMessage({
          text: "Password reset successful. Redirecting...",
          type: "success",
        });
        setTimeout(() => navigate("/"), 3000);
      } else {
        setMessage({
          text: "Password reset failed. Try again.",
          type: "error",
        });
      }
    } catch (error: unknown) {
      const errorMessage = isAxiosError(error)
        ? error.response?.data?.message || error.message
        : "Password reset failed. Try again.";
      setMessage({ text: errorMessage, type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="auth-page">
      <div className="flex flex-col justify-center min-h-full px-6 py-12 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-sm">
          <div className="items-center text-center">
            <Logo className="w-18 h-18 mx-auto" aria-label="Logo" />
          </div>
          <h2 className="mt-2 tracking-tight text-center text-fg-title">
            Reset Password
          </h2>
        </div>
        {message.text && (
          <div className="sm:mx-auto sm:w-full sm:max-w-sm mt-4 text-center">
            <p
              className={`${message.type === "success" ? "text-success" : "text-error"}`}
            >
              {message.text}
            </p>
          </div>
        )}
        <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
          <form noValidate onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="password" className="text-fg-medium">
                New Password
              </label>
              <div className="mt-2 relative">
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    className="input-custom pr-10"
                    placeholder="New Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center justify-center focus:outline-none"
                    tabIndex={-1}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <VisibilityOffOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    ) : (
                      <VisibilityOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <label htmlFor="passwordConfirm" className="text-fg-medium">
                Confirm Password
              </label>
              <div className="mt-2 relative">
                <div className="relative">
                  <input
                    id="password_confirm"
                    type={showPasswordConfirm ? "text" : "password"}
                    className="input-custom pr-10"
                    placeholder="Confirm Password"
                    value={password_confirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-3 flex items-center justify-center focus:outline-none"
                    tabIndex={-1}
                    onClick={() => setShowPasswordConfirm(!showPasswordConfirm)}
                  >
                    {showPasswordConfirm ? (
                      <VisibilityOffOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    ) : (
                      <VisibilityOutlinedIcon className="icon-sm text-fg-muted-medium" />
                    )}
                  </button>
                </div>
              </div>
            </div>
            <div>
              <Button
                type="submit"
                variant="primary"
                size="lg"
                fullWidth={true}
                disabled={isSubmitting}
              >
                {isSubmitting ? "Resetting..." : "Reset Password"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
